import { Component, Input, OnChanges, OnDestroy, OnInit, Output, EventEmitter, AfterViewInit, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { DocumentService } from 'src/app/services/document.service';
import { FrontendLoggerService } from 'src/app/services/frontend-logger.service';
import { ThemeService } from 'src/app/services/theme/theme.service';
import { LanguageService } from 'src/app/services/language/language.service';
import { groupSmallSlices } from '../shared/chart-slices.util';
import { buildDomainDonutOptions, buildSubdomainSlices } from './domain-donut-options.builder';

interface DomainNode {
  id: string;
  label: string;
  domain: string;
  type: string;
  date: string;
  subdomain?: string; // Soporta subdominio/subcategoría opcional
}

interface DomainLink {
  source: string;
  target: string;
}

interface DomainMap {
  domains: string[];
  nodes: DomainNode[];
  links: DomainLink[];
}

@Component({
    selector: 'app-document-network',
    templateUrl: './document-network.component.html',
    styleUrls: ['./document-network.component.scss'],
    standalone: false
})
export class DocumentNetworkComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  /**
   * El tab que contiene esta vista. ECharts se inicializa con el tamano que
   * tenga el contenedor en ese instante y, si el tab aun no es visible, se queda
   * clavado en su tamano por defecto (400px) ignorando el CSS.
   */
  @Input() active = true;

  private chartInstance: { resize: () => void } | null = null;
  private subdomainChartInstance: { resize: () => void } | null = null;
  private themeSubscription?: Subscription;

  isLoading = true;
  errorMessage = '';
  domainMap: DomainMap = { domains: [], nodes: [], links: [] };

  selectedDomain: string | null = null;
  selectedSubdomain: string | null = null;
  chartOptions: any;
  chartData: any[] = [];
  subdomains: string[] = [];
  subdomainChartOptions: any;
  subdomainChartData: any[] = [];
  chartColors: string[] = ['#22c55e', '#7c4dff', '#f59e0b', '#8e24aa', '#bdbdbd'];
  subdomainColors: { [key: string]: string } = {};
  domainColors: { [key: string]: string } = {};

  /** Texto del buscador de la lista lateral de dominios. */
  domainQuery = '';
  /** Dominios que la lista lateral muestra ahora mismo (ya filtrados). */
  visibleDomains: string[] = [];

  /** Sectores visibles antes de agrupar la cola: por encima el donut se vuelve ilegible. */
  private static readonly MAX_PIE_SLICES = 12;
  /** Gris neutro, legible sobre fondo claro y oscuro, para el sector "otros". */
  private static readonly GROUPED_SLICE_COLOR = '#94a3b8';

  @Output() domainSelected = new EventEmitter<string>();

  constructor(
    private readonly documentService: DocumentService,
    public themeService: ThemeService,
    public languageService: LanguageService,
    private readonly frontendLogger: FrontendLoggerService
  ) {}

  ngOnInit(): void {
    this.frontendLogger.info('[DocumentNetworkComponent] Inicializando');
    this.clearAllFilters();
    this.loadDomainMap();
    this.subscribeToThemeChanges();
  }

  /**
   * ECharts pinta sobre un canvas, no con CSS: el color de las etiquetas queda
   * congelado en las opciones. Sin esta suscripcion, al cambiar de tema los
   * graficos conservaban los colores del tema anterior hasta recargar la pagina.
   */
  subscribeToThemeChanges(): void {
    this.themeSubscription = this.themeService.currentTheme$
      .pipe(skip(1))
      .subscribe(() => this.rebuildChartsForTheme());
  }

  private rebuildChartsForTheme(): void {
    if (this.domainMap.domains.length) {
      this.setupPieChart();
    }
    if (this.selectedDomain) {
      this.setupSubdomainPieChart();
    }
  }

  ngOnDestroy(): void {
    this.themeSubscription?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['active'] && this.active) {
      this.resizeCharts();
    }
  }

  /** ngx-echarts entrega aqui la instancia real del grafico de dominios. */
  onChartInit(instance: { resize: () => void }): void {
    this.chartInstance = instance;
  }

  /** Idem para el grafico de subdominios (segundo nivel). */
  onSubdomainChartInit(instance: { resize: () => void }): void {
    this.subdomainChartInstance = instance;
  }

  /**
   * Se espera a que termine la animacion de entrada del tab: durante la
   * transicion el contenedor todavia no tiene su tamano definitivo.
   */
  private resizeCharts(): void {
    setTimeout(() => {
      this.chartInstance?.resize();
      this.subdomainChartInstance?.resize();
    }, 300);
  }

  ngAfterViewInit(): void {
    this.refreshLayout();
  }

  loadDomainMap() {
    this.isLoading = true;
    this.documentService.getDocumentDomainMap().subscribe({
      next: (domainMap) => {
        this.domainMap = domainMap;
        this.setupPieChart();
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        this.errorMessage = 'Error al cargar el mapa de dominios: ' + (error.message || 'Error desconocido');
      }
    });
  }

  /**
   * Filtra la lista lateral de dominios. La cuenta real tiene cientos, asi que
   * sin buscador la unica forma de llegar a uno concreto era desplazarse por
   * toda la columna.
   */
  onDomainQueryChange(query: string): void {
    this.domainQuery = query;
    this.applyDomainQuery();
  }

  clearDomainQuery(): void {
    this.onDomainQueryChange('');
  }

  private applyDomainQuery(): void {
    const query = this.domainQuery.trim().toLowerCase();
    this.visibleDomains = query
      ? this.domainMap.domains.filter(domain => domain.toLowerCase().includes(query))
      : [...this.domainMap.domains];
  }

  /** Color estable por dominio: se precalcula para no hacer un indexOf por chip. */
  private indexDomainColors(): void {
    this.domainColors = {};
    this.domainMap.domains.forEach((domain, i) => {
      this.domainColors[domain] = this.chartColors[i % this.chartColors.length];
    });
  }

  setupPieChart() {
    this.indexDomainColors();
    this.applyDomainQuery();

    // Un solo recorrido de nodos: con cientos de dominios, contar filtrando por
    // cada uno recorria la lista completa una vez por dominio.
    const countByDomain = new Map<string, number>();
    for (const node of this.domainMap.nodes) {
      countByDomain.set(node.domain, (countByDomain.get(node.domain) ?? 0) + 1);
    }

    const slices = groupSmallSlices(
      this.domainMap.domains.map(domain => ({
        name: domain,
        value: countByDomain.get(domain) ?? 0
      })),
      DocumentNetworkComponent.MAX_PIE_SLICES,
      this.translate('documents.other_domains')
    );

    this.chartData = slices.map((slice, i) => ({
      name: slice.name,
      value: slice.value,
      isGrouped: slice.isGrouped === true,
      groupedCount: slice.groupedCount ?? 0,
      itemStyle: {
        color: slice.isGrouped
          ? DocumentNetworkComponent.GROUPED_SLICE_COLOR
          : this.chartColors[i % this.chartColors.length]
      }
    }));
    this.chartOptions = buildDomainDonutOptions({
      seriesName: this.translate('documents.domains'),
      data: this.chartData,
      isDark: this.themeService.getCurrentTheme() === 'dark',
      tooltipFormatter: (params: any) => this.formatDomainTooltip(params)
    });
  }

  /** El sector agrupado aclara cuantos dominios resume; el resto va tal cual. */
  private formatDomainTooltip(params: any): string {
    const documents = `${params.value} ${this.translate('documents.documents_word')}`;
    if (params.data?.isGrouped) {
      return `${params.name} (${params.data.groupedCount}): ${documents}`;
    }
    return `${params.name}: ${documents}`;
  }

  onDomainFilterClick(domain: string) {
    this.selectedDomain = domain;
    this.setupSubdomainPieChart();
  }

  setupSubdomainPieChart() {
    const nodes = this.domainMap.nodes.filter(n => n.domain === this.selectedDomain);
    const slices = buildSubdomainSlices(nodes, this.chartColors);

    this.subdomains = slices.names;
    this.subdomainColors = slices.colorByName;
    this.subdomainChartData = slices.data;
    this.subdomainChartOptions = buildDomainDonutOptions({
      seriesName: this.translate('documents.subdomains'),
      data: this.subdomainChartData,
      isDark: this.themeService.getCurrentTheme() === 'dark',
      tooltipFormatter: (params: any) =>
        `${params.name}: ${params.value} ${this.translate('documents.documents_word')}`
    });
  }

  onSubdomainFilterClick(sub: string) {
    this.selectedSubdomain = sub;
    // Emitir evento para que el padre (tabs) cambie de pestaña y pase el filtro
    this.domainSelected.emit(this.selectedDomain + '|' + this.selectedSubdomain);
  }

  resetPieChart() {
    this.selectedDomain = null;
    this.selectedSubdomain = null;
    this.setupPieChart();
  }

  translate(key: string, params?: any): string {
    let translation = this.languageService.translate(key);
    if (params) {
      Object.keys(params).forEach(param => {
        translation = translation.replace(`{{${param}}}`, params[param]);
      });
    }
    return translation;
  }

  public refreshLayout() {
    globalThis.dispatchEvent(new Event('resize'));
  }

  clearAllFilters() {
    this.selectedDomain = null;
    this.selectedSubdomain = null;
    this.setupPieChart();
  }
}
